import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Paper,
  Select,
  Table,
  Tabs,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconDice5, IconEye, IconEyeOff, IconRefresh } from '@tabler/icons-react'
import { useNavigate, useParams } from 'react-router-dom'
import { Navigation } from './components'
import { useClient } from '@/hooks/useClient'
import { AUTH_AUDIT_EVENT_TYPE_VALUES, OTP_OVERRIDE_MAX_DAYS } from '@/model/middleware-types'

/**
 * Super Admin → Auth: the /v2/auth gateway console (PD-1512, AUTH_MIGRATION.md §1a.4).
 * KPI strip on top (each card opens its tab); one table per subtab, each tab with its
 * own route (/super/auth/:tab — Overview is the default for a missing or unknown tab) and
 * an Overview tab previewing the 5 most recent rows of the live-activity tables (sessions,
 * logins in flight, lockouts, activity — titles and "n of m — see all" link into the full
 * tabs; trusted devices and 2FA overrides are configuration, tab-only). Auto refresh is
 * on by default. DB-backed tables (sessions, trusted devices, activity)
 * follow the model paging pattern (like PatientList → listPatients): request
 * `page: { offset, count, limit }`, response echoes it with `size`/`total`; 100 rows per
 * page, more loading as the sentinel row scrolls into view — no upper bound. Redis-backed
 * lists (lockouts, in-flight logins) are fetched whole (TTL'd and small) and windowed
 * the same way client-side.
 * 2FA overrides (PD-1641): the fixed-code exception list — an account on it still gets
 * the real OTP delivered, but only its fixed code verifies. Redis-backed with a mandatory
 * expiry (an exact instant, at most 365 days out); the code is shown masked with a
 * reveal toggle.
 * All endpoints enforce super-admin SERVER-side; this page is just the window.
 */

const PAGE_SIZE = 100
const PREVIEW_SIZE = 5
const AUTO_REFRESH_SECONDS = 15

// Tab order = Overview preview order; configuration tabs (trusted devices, overrides) last.
const TAB_VALUES = [
  'overview',
  'sessions',
  'inflight',
  'lockouts',
  'activity',
  'trusted',
  'overrides',
] as const
type TabValue = (typeof TAB_VALUES)[number]

type Overview = {
  sessions: number
  providerSessions: number
  patientSessions: number
  activeInLast5Min: number
  lockedAccounts: number
  loginsInFlight: number
  trustedDevices: number
  otpOverrides: number
}

type OtpOverrideRow = {
  emailHash: string
  email: string
  code: string
  setBy: string
  setByName?: string
  setAt: string
  expiresAt: string
}

const DAY_MS = 86_400_000
const OTP_OVERRIDE_TTL_PRESETS = [
  { value: '1', label: '1 day' },
  { value: '7', label: '1 week' },
  { value: '30', label: '1 month' },
  { value: '365', label: '1 year' },
  { value: 'custom', label: 'Pick a date…' },
]

type SessionRow = {
  id: number
  userId: string
  profileRef: string
  profileName?: string
  sessionKind: 'provider' | 'patient'
  clientType: 'web' | 'native'
  deviceLabel?: string
  createdIp?: string
  createdAt: string
  lastRefreshAt: string
  expiresAt: string
  expired: boolean
}

type TrustedDeviceRow = {
  id: number
  userId: string
  userEmail?: string
  deviceLabel?: string
  createdAt: string
  expiresAt: string
}

type LockoutRow = {
  emailHash: string
  failures: number
  locked: boolean
  windowMsRemaining: number
  userEmail?: string
  userId?: string
}

type ChallengeRow = {
  id: string
  sessionKind: string
  step: string
  maskedDestination?: string
  attemptsLeft?: number
  msRemaining: number
}

type AuditRow = {
  id: number
  at: string
  eventType: string
  userEmail?: string
  userId?: string
  adminRef?: string
  adminName?: string
  sessionId?: number
  ip?: string
  detail?: Record<string, unknown>
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))}s ago`
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`
  return `${Math.round(ms / 86_400_000)}d ago`
}

function formatLocal(iso: string): string {
  return new Date(iso).toLocaleString()
}

function minutes(ms: number): string {
  return `${Math.max(0, Math.ceil(ms / 60_000))} min`
}

/** Six random digits (the OTP shape) for the "random code" button. */
function randomOtpCode(): string {
  return Array.from(crypto.getRandomValues(new Uint32Array(6)), (n) => String(n % 10)).join('')
}

/** The exact instant a picked calendar day ends, in the admin's local time (the picker
 *  hands back 'YYYY-MM-DD'; a date-time string without offset parses as local). */
function endOfLocalDay(ymd: string): string {
  return new Date(`${ymd}T23:59:59.999`).toISOString()
}

function addDays(days: number): Date {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d
}

/** Request shape for one page of PAGE_SIZE rows at `offset` (model ModelPageRequest). */
function pageReq(offset: number) {
  return { offset, count: PAGE_SIZE, limit: Number.MAX_SAFE_INTEGER }
}

/**
 * Offset-paged list following the model paging pattern (PatientList → listPatients):
 * `fetchPage(offset)` sends `page: { offset, count, limit }` and resolves the
 * `{ page: { total, … }, data }` envelope; there is more while loaded < total.
 */
function usePagedList<T>(
  fetchPage: (offset: number) => Promise<{ page: { total: number }; data: T[] }>,
) {
  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const itemsRef = useRef(items)
  itemsRef.current = items
  const loadingRef = useRef(false)

  const load = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current) return
      loadingRef.current = true
      try {
        const base = reset ? [] : itemsRef.current
        const res = await fetchPage(base.length)
        setItems([...base, ...res.data])
        setTotal(res.page.total)
      } catch (err) {
        showNotification({ color: 'red', message: (err as Error).message || 'Failed to load' })
      } finally {
        loadingRef.current = false
      }
    },
    [fetchPage],
  )

  return {
    items,
    total,
    hasMore: items.length < total,
    loadMore: useCallback(() => void load(false), [load]),
    reset: useCallback(() => void load(true), [load]),
  }
}

/** Whole-list fetch with client-side windowing (Redis-backed panels — small, TTL'd). */
function useWindowedList<T>(fetchAll: () => Promise<T[]>) {
  const [all, setAll] = useState<T[]>([])
  const [visible, setVisible] = useState(PAGE_SIZE)

  const reset = useCallback(() => {
    fetchAll()
      .then((rows) => {
        setAll(rows)
        setVisible(PAGE_SIZE)
      })
      .catch((err) =>
        showNotification({ color: 'red', message: (err as Error).message || 'Failed to load' }),
      )
  }, [fetchAll])

  return {
    items: all.slice(0, visible),
    total: all.length,
    hasMore: visible < all.length,
    loadMore: useCallback(() => setVisible((v) => v + PAGE_SIZE), []),
    reset,
  }
}

/** Invisible row that requests the next page when scrolled into view. */
function ScrollSentinel({ enabled, onVisible }: { enabled: boolean; onVisible: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined' || !ref.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) onVisible()
    })
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [enabled, onVisible])
  if (!enabled) return null
  return <div ref={ref} className="h-1" data-testid="scroll-sentinel" />
}

function SessionsTable({
  items,
  onForceLogout,
  onForceLogoutAll,
}: {
  items: SessionRow[]
  onForceLogout: (s: SessionRow) => void
  onForceLogoutAll: (s: SessionRow) => void
}) {
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>User</Table.Th>
          <Table.Th>Device</Table.Th>
          <Table.Th>Signed in</Table.Th>
          <Table.Th>Last activity</Table.Th>
          <Table.Th>Expires</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={6} className="text-center text-sm text-gray-500">
              No live sessions
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((s) => {
          const activeNow =
            !s.expired && Date.now() - new Date(s.lastRefreshAt).getTime() < 2 * 60_000
          return (
            <Table.Tr key={s.id}>
              <Table.Td>
                <div className="flex items-center gap-2">
                  {activeNow && (
                    <Tooltip label="Refreshed within 2 minutes">
                      <span className="block h-2 w-2 rounded-full bg-green-500" />
                    </Tooltip>
                  )}
                  <span>{s.profileName ?? s.profileRef}</span>
                  <Badge size="xs" variant="light">
                    {s.sessionKind}
                  </Badge>
                  {s.clientType === 'native' && (
                    <Tooltip label="Signed in from the mobile app (native credential channel)">
                      <Badge size="xs" variant="filled" color="indigo">
                        mobile
                      </Badge>
                    </Tooltip>
                  )}
                  {s.expired && (
                    <Tooltip label="Dead session awaiting the hourly purge — its tokens can no longer be used">
                      <Badge size="xs" variant="light" color="gray">
                        expired
                      </Badge>
                    </Tooltip>
                  )}
                </div>
              </Table.Td>
              <Table.Td>
                {s.deviceLabel ?? '—'}
                {s.createdIp ? ` · ${s.createdIp}` : ''}
              </Table.Td>
              <Table.Td>{formatLocal(s.createdAt)}</Table.Td>
              <Table.Td>{timeAgo(s.lastRefreshAt)}</Table.Td>
              <Table.Td className={s.expired ? 'text-gray-400' : undefined}>
                {formatLocal(s.expiresAt)}
              </Table.Td>
              <Table.Td>
                <div className="flex justify-end gap-2">
                  <Button size="xs" color="red" variant="light" onClick={() => onForceLogout(s)}>
                    Force logout
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="subtle"
                    onClick={() => onForceLogoutAll(s)}
                  >
                    All devices
                  </Button>
                </div>
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

function LockoutsTable({
  items,
  onClear,
}: {
  items: LockoutRow[]
  onClear: (l: LockoutRow) => void
}) {
  return (
    <Table striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Account</Table.Th>
          <Table.Th>Failures</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Window resets in</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={5} className="text-center text-sm text-gray-500">
              No accounts counting failures
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((l) => (
          <Table.Tr key={l.emailHash}>
            <Table.Td>
              {l.userEmail ?? (
                <Tooltip label="Failed attempts against an email that matches no account — attack noise or typos">
                  <span className="text-gray-500 italic">not an account</span>
                </Tooltip>
              )}
            </Table.Td>
            <Table.Td>{l.failures}</Table.Td>
            <Table.Td>
              {l.locked ? (
                <Badge color="red">locked</Badge>
              ) : (
                <Badge color="yellow">counting</Badge>
              )}
            </Table.Td>
            <Table.Td>{minutes(l.windowMsRemaining)}</Table.Td>
            <Table.Td className="text-right">
              <Button size="xs" variant="light" onClick={() => onClear(l)}>
                Clear
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function TrustedDevicesTable({
  items,
  onRevoke,
}: {
  items: TrustedDeviceRow[]
  onRevoke: (d: TrustedDeviceRow) => void
}) {
  return (
    <Table striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>User</Table.Th>
          <Table.Th>Device</Table.Th>
          <Table.Th>Expires</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={4} className="text-center text-sm text-gray-500">
              No trusted devices
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((d) => (
          <Table.Tr key={d.id}>
            <Table.Td>{d.userEmail ?? d.userId}</Table.Td>
            <Table.Td>{d.deviceLabel ?? '—'}</Table.Td>
            <Table.Td>{formatLocal(d.expiresAt)}</Table.Td>
            <Table.Td className="text-right">
              <Button size="xs" color="red" variant="subtle" onClick={() => onRevoke(d)}>
                Revoke
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function ChallengesTable({
  items,
  onVoid,
}: {
  items: ChallengeRow[]
  onVoid: (c: ChallengeRow) => void
}) {
  return (
    <Table striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Step</Table.Th>
          <Table.Th>Code sent to</Table.Th>
          <Table.Th>Attempts left</Table.Th>
          <Table.Th>Expires in</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={5} className="text-center text-sm text-gray-500">
              No logins in flight
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((c) => (
          <Table.Tr key={c.id}>
            <Table.Td>{c.step.replace(/_/g, ' ')}</Table.Td>
            <Table.Td>{c.maskedDestination ?? '—'}</Table.Td>
            <Table.Td>{c.attemptsLeft ?? '—'}</Table.Td>
            <Table.Td>{minutes(c.msRemaining)}</Table.Td>
            <Table.Td className="text-right">
              <Button size="xs" color="red" variant="subtle" onClick={() => onVoid(c)}>
                Void
              </Button>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function ActivityTable({ items }: { items: AuditRow[] }) {
  return (
    <Table striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>When</Table.Th>
          <Table.Th>Event</Table.Th>
          <Table.Th>Actor</Table.Th>
          <Table.Th>User</Table.Th>
          <Table.Th>IP</Table.Th>
          <Table.Th>Detail</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={6} className="text-center text-sm text-gray-500">
              No events
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((e) => (
          <Table.Tr key={e.id}>
            <Table.Td>
              <Tooltip label={formatLocal(e.at)}>
                <span>{timeAgo(e.at)}</span>
              </Tooltip>
            </Table.Td>
            <Table.Td>
              <Badge
                size="sm"
                variant="light"
                color={
                  e.eventType.includes('failed') ||
                  e.eventType.includes('lockout') ||
                  e.eventType.includes('revoked')
                    ? 'red'
                    : e.eventType.startsWith('admin')
                      ? 'grape'
                      : 'teal'
                }
              >
                {e.eventType}
              </Badge>
            </Table.Td>
            {/* `||`: a profile with only HumanName.text resolves to an empty name. */}
            <Table.Td>{e.adminName || e.adminRef || '—'}</Table.Td>
            <Table.Td>{e.userEmail ?? e.userId ?? '—'}</Table.Td>
            <Table.Td>{e.ip ?? '—'}</Table.Td>
            <Table.Td className="max-w-[280px] truncate text-xs text-gray-500">
              {e.detail ? JSON.stringify(e.detail) : ''}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function OtpOverridesTable({
  items,
  onRemove,
}: {
  items: OtpOverrideRow[]
  onRemove: (o: OtpOverrideRow) => void
}) {
  const [revealed, setRevealed] = useState<Set<string>>(new Set())
  const toggle = (hash: string) =>
    setRevealed((prev) => {
      const next = new Set(prev)
      if (next.has(hash)) next.delete(hash)
      else next.add(hash)
      return next
    })
  return (
    <Table striped>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Account</Table.Th>
          <Table.Th>Fixed code</Table.Th>
          <Table.Th>Set by</Table.Th>
          <Table.Th>Set</Table.Th>
          <Table.Th>Expires</Table.Th>
          <Table.Th />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.length === 0 && (
          <Table.Tr>
            <Table.Td colSpan={6} className="text-center text-sm text-gray-500">
              No 2FA overrides
            </Table.Td>
          </Table.Tr>
        )}
        {items.map((o) => {
          const shown = revealed.has(o.emailHash)
          return (
            <Table.Tr key={o.emailHash}>
              <Table.Td>{o.email}</Table.Td>
              <Table.Td>
                <div className="flex items-center gap-1">
                  <span className="font-mono tracking-widest">{shown ? o.code : '••••••'}</span>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label={shown ? 'Hide code' : 'Show code'}
                    onClick={() => toggle(o.emailHash)}
                  >
                    {shown ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                  </ActionIcon>
                </div>
              </Table.Td>
              <Table.Td>{o.setByName ?? o.setBy}</Table.Td>
              <Table.Td>{formatLocal(o.setAt)}</Table.Td>
              <Table.Td>{formatLocal(o.expiresAt)}</Table.Td>
              <Table.Td className="text-right">
                <Button size="xs" color="red" variant="subtle" onClick={() => onRemove(o)}>
                  Remove
                </Button>
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

/**
 * Add/replace a fixed 2FA code: account email, 6-digit code (typed or rolled), and a
 * mandatory expiry — preset spans or a calendar date within 1–365 days.
 */
function AddOtpOverrideForm({
  onSubmit,
}: {
  /** Resolves true on success — the form only clears then. */
  onSubmit: (input: { email: string; code: string; expiresAt: string }) => Promise<boolean>
}) {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [ttl, setTtl] = useState<string>('7')
  const [until, setUntil] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Presets expire exactly N days from submit; a picked date expires at the end of that
  // day. Either way the wire carries the instant, so the table shows what was chosen.
  const expiresAt = ttl === 'custom' ? (until ? endOfLocalDay(until) : undefined) : undefined
  const valid =
    /\S+@\S+\.\S+/.test(email.trim()) && /^\d{6}$/.test(code) && (ttl !== 'custom' || !!until)

  const submit = async () => {
    if (!valid) return
    setBusy(true)
    try {
      const ok = await onSubmit({
        email: email.trim(),
        code,
        expiresAt: expiresAt ?? new Date(Date.now() + Number(ttl) * DAY_MS).toISOString(),
      })
      if (ok) {
        setEmail('')
        setCode('')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className="mb-3 flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <TextInput
        size="xs"
        label="Account email"
        placeholder="reviewer@example.com"
        className="min-w-64 flex-1"
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <TextInput
        size="xs"
        label="Fixed code"
        placeholder="6 digits"
        className="w-32"
        classNames={{ input: 'font-mono tracking-widest' }}
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
        rightSection={
          <Tooltip label="Random code">
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              aria-label="Random code"
              onClick={() => setCode(randomOtpCode())}
            >
              <IconDice5 size={14} />
            </ActionIcon>
          </Tooltip>
        }
      />
      <Select
        size="xs"
        label="Expires in"
        className="w-36"
        data={OTP_OVERRIDE_TTL_PRESETS}
        value={ttl}
        allowDeselect={false}
        onChange={(v) => setTtl(v ?? '7')}
      />
      {ttl === 'custom' && (
        <DatePickerInput
          size="xs"
          label="Expiry date"
          placeholder="Pick a date"
          className="w-40"
          minDate={addDays(1)}
          // One day short of the server's absolute maximum: a pick expires at the END of
          // that day, which must still be within OTP_OVERRIDE_MAX_DAYS of now.
          maxDate={addDays(OTP_OVERRIDE_MAX_DAYS - 1)}
          value={until}
          onChange={setUntil}
        />
      )}
      <Button size="xs" type="submit" disabled={!valid} loading={busy}>
        Set override
      </Button>
    </form>
  )
}

/** Overview preview block: 5 most recent rows, title and "n of m" both open the full tab. */
function OverviewSection({
  title,
  shown,
  total,
  onSeeAll,
  children,
}: {
  title: string
  shown: number
  total: number
  onSeeAll: () => void
  children: React.ReactNode
}) {
  return (
    <div className="mt-4 first:mt-0">
      <div className="mb-1 flex items-baseline justify-between">
        {/* Buttons, not href-less <a>s, so they are focusable and keyboard-activatable. */}
        <Anchor
          component="button"
          type="button"
          size="sm"
          fw={600}
          c="inherit"
          underline="hover"
          onClick={onSeeAll}
        >
          {title}
        </Anchor>
        <Anchor component="button" type="button" size="xs" onClick={onSeeAll}>
          {shown} of {total} — see all
        </Anchor>
      </div>
      <Paper withBorder>{children}</Paper>
    </div>
  )
}

export function AuthConsole() {
  const client = useClient()
  const navigate = useNavigate()
  const { tab } = useParams()
  // Overview is the default: no tab in the URL, or an unknown one, lands there.
  const activeTab: TabValue = (TAB_VALUES as readonly string[]).includes(tab ?? '')
    ? (tab as TabValue)
    : 'overview'
  // Overview lives at the bare /super/auth — no 'overview' segment in the URL.
  const goTo = useCallback(
    (value: TabValue) => navigate(value === 'overview' ? '/super/auth' : `/super/auth/${value}`),
    [navigate],
  )

  const [overview, setOverview] = useState<Overview>()
  const [eventType, setEventType] = useState<string | null>(null)

  const sessions = usePagedList<SessionRow>(
    useCallback((offset) => client.authAdminSessions({ page: pageReq(offset) }), [client]),
  )
  const devices = usePagedList<TrustedDeviceRow>(
    useCallback((offset) => client.authAdminTrustedDevices({ page: pageReq(offset) }), [client]),
  )
  const events = usePagedList<AuditRow>(
    useCallback(
      (offset) =>
        client.authAdminAuditEvents({ page: pageReq(offset), eventType: eventType ?? undefined }),
      [client, eventType],
    ),
  )
  const lockouts = useWindowedList<LockoutRow>(
    useCallback(() => client.authAdminLockouts({}).then((r) => r.lockouts), [client]),
  )
  const challenges = useWindowedList<ChallengeRow>(
    useCallback(() => client.authAdminChallenges({}).then((r) => r.challenges), [client]),
  )
  const overrides = useWindowedList<OtpOverrideRow>(
    useCallback(() => client.authAdminOtpOverrides({}).then((r) => r.overrides), [client]),
  )

  const refreshOverview = useCallback(() => {
    client
      .authAdminOverview({})
      .then(setOverview)
      .catch((err) =>
        showNotification({ color: 'red', message: (err as Error).message || 'Failed to load' }),
      )
  }, [client])

  // Only the lists the current tab shows are (re)fetched: Overview previews four of them,
  // every other tab exactly one. Keeps the 15 s tick to the visible data.
  const refreshLists = useCallback(
    (tab: TabValue) => {
      const onOverview = tab === 'overview'
      if (onOverview || tab === 'sessions') sessions.reset()
      if (onOverview || tab === 'inflight') challenges.reset()
      if (onOverview || tab === 'lockouts') lockouts.reset()
      if (onOverview || tab === 'activity') events.reset()
      if (tab === 'trusted') devices.reset()
      if (tab === 'overrides') overrides.reset()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sessions.reset,
      devices.reset,
      lockouts.reset,
      challenges.reset,
      events.reset,
      overrides.reset,
    ],
  )

  const refreshAll = useCallback(() => {
    refreshOverview()
    refreshLists(activeTab)
  }, [refreshOverview, refreshLists, activeTab])

  // Initial load, and whenever the tab changes (its list may not be loaded yet).
  useEffect(() => {
    refreshAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Auto refresh (on by default): tick a countdown every second while enabled; hitting 0
  // refreshes and rearms. refreshAll is read through a ref so a changing identity
  // (event-type filter) doesn't restart the countdown.
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECONDS)
  const refreshAllRef = useRef(refreshAll)
  refreshAllRef.current = refreshAll

  useEffect(() => {
    if (!autoRefresh) return
    setCountdown(AUTO_REFRESH_SECONDS)
    const timer = setInterval(() => setCountdown((s) => s - 1), 1000)
    return () => clearInterval(timer)
  }, [autoRefresh])

  useEffect(() => {
    if (!autoRefresh || countdown > 0) return
    refreshAllRef.current()
    setCountdown(AUTO_REFRESH_SECONDS)
  }, [autoRefresh, countdown])

  // Event-type filter restarts the activity feed from the top.
  useEffect(() => {
    events.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType])

  /** Runs an admin action with toast feedback; resolves whether it succeeded. */
  const act = useCallback(
    async (action: () => Promise<unknown>, success: string): Promise<boolean> => {
      try {
        await action()
        showNotification({ color: 'green', message: success })
        refreshAll()
        return true
      } catch (err) {
        showNotification({ color: 'red', message: (err as Error).message || 'Action failed' })
        return false
      }
    },
    [refreshAll],
  )

  const forceLogout = (session: SessionRow) =>
    void act(() => client.authAdminRevokeSession({ sessionId: session.id }), 'Session revoked')

  // Revocation is atomic per session (upstream Login first): failed ones stay listed
  // and are retryable, so a partial result gets a warning instead of clean success.
  const forceLogoutAll = useCallback(
    async (session: SessionRow) => {
      try {
        const res = await client.authAdminRevokeUserSessions({ userId: session.userId })
        if (res.failed > 0) {
          showNotification({
            color: 'yellow',
            message: `${res.revoked} session(s) revoked; ${res.failed} failed upstream and remain listed — retry`,
          })
        } else {
          showNotification({ color: 'green', message: 'All sessions revoked' })
        }
        refreshAll()
      } catch (err) {
        showNotification({ color: 'red', message: (err as Error).message || 'Action failed' })
      }
    },
    [client, refreshAll],
  )

  const confirmForceLogout = (session: SessionRow) =>
    openConfirmModal({
      title: 'Force logout',
      children: (
        <p className="text-sm">
          End the session for <b>{session.profileName ?? session.profileRef}</b> on{' '}
          {session.deviceLabel ?? 'unknown device'}? The Medplum login is revoked too, so their
          access token dies immediately.
        </p>
      ),
      labels: { confirm: 'Force logout', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => forceLogout(session),
    })

  const confirmForceLogoutAll = (session: SessionRow) =>
    openConfirmModal({
      title: 'Force logout — all sessions',
      children: (
        <p className="text-sm">
          End <b>every</b> session for {session.profileName ?? session.profileRef}, on all devices?
        </p>
      ),
      labels: { confirm: 'Force logout all', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => void forceLogoutAll(session),
    })

  const clearLockout = (l: LockoutRow) =>
    void act(() => client.authAdminClearLockout({ emailHash: l.emailHash }), 'Lockout cleared')
  const revokeTrustedDevice = (d: TrustedDeviceRow) =>
    void act(
      () => client.authAdminRevokeTrustedDevice({ id: d.id }),
      'Device trust revoked — next login re-prompts 2FA',
    )
  const voidChallenge = (c: ChallengeRow) =>
    void act(() => client.authAdminVoidChallenge({ id: c.id }), 'Challenge voided')
  const setOtpOverride = (input: { email: string; code: string; expiresAt: string }) =>
    act(
      () => client.authAdminSetOtpOverride(input),
      `2FA override set for ${input.email} — only the fixed code verifies now`,
    )
  const confirmRemoveOtpOverride = (o: OtpOverrideRow) =>
    openConfirmModal({
      title: 'Remove 2FA override',
      children: (
        <p className="text-sm">
          Remove the fixed code for <b>{o.email}</b>? Their next login expects the delivered code
          again.
        </p>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        void act(
          () => client.authAdminRemoveOtpOverride({ emailHash: o.emailHash }),
          '2FA override removed',
        ),
    })

  // KPI cards; each opens the tab that details it.
  const stats: Array<[string, number | undefined, TabValue]> = [
    ['Live sessions', overview?.sessions, 'sessions'],
    ['Active (5 min)', overview?.activeInLast5Min, 'sessions'],
    ['Locked accounts', overview?.lockedAccounts, 'lockouts'],
    ['Logins in flight', overview?.loginsInFlight, 'inflight'],
    ['Trusted devices', overview?.trustedDevices, 'trusted'],
    ['2FA overrides', overview?.otpOverrides, 'overrides'],
  ]

  const previews: Array<{
    tab: TabValue
    title: string
    shown: number
    total: number
    table: React.ReactNode
  }> = [
    {
      tab: 'sessions',
      title: 'Live sessions',
      shown: Math.min(PREVIEW_SIZE, sessions.items.length),
      total: sessions.total,
      table: (
        <SessionsTable
          items={sessions.items.slice(0, PREVIEW_SIZE)}
          onForceLogout={confirmForceLogout}
          onForceLogoutAll={confirmForceLogoutAll}
        />
      ),
    },
    {
      tab: 'inflight',
      title: 'Logins in flight',
      shown: Math.min(PREVIEW_SIZE, challenges.total),
      total: challenges.total,
      table: (
        <ChallengesTable items={challenges.items.slice(0, PREVIEW_SIZE)} onVoid={voidChallenge} />
      ),
    },
    {
      tab: 'lockouts',
      title: 'Lockouts',
      shown: Math.min(PREVIEW_SIZE, lockouts.total),
      total: lockouts.total,
      table: <LockoutsTable items={lockouts.items.slice(0, PREVIEW_SIZE)} onClear={clearLockout} />,
    },
    {
      tab: 'activity',
      title: 'Activity',
      shown: Math.min(PREVIEW_SIZE, events.items.length),
      total: events.total,
      table: <ActivityTable items={events.items.slice(0, PREVIEW_SIZE)} />,
    },
  ]

  return (
    <div className="p-6">
      <Navigation />

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-6">
        {stats.map(([label, value, target]) => (
          <Paper
            key={label}
            component="button"
            type="button"
            withBorder
            p="sm"
            className="cursor-pointer text-center hover:bg-gray-50"
            onClick={() => goTo(target)}
          >
            {/* spans: a <button> only permits phrasing content */}
            <span className="block text-2xl font-bold">{value ?? '—'}</span>
            <span className="block text-xs text-gray-500">{label}</span>
          </Paper>
        ))}
      </div>

      <Tabs
        value={activeTab}
        onChange={(value) => goTo((value as TabValue) ?? 'overview')}
        className="mt-6"
        keepMounted={false}
      >
        <div className="flex items-center justify-between">
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="sessions">Live sessions</Tabs.Tab>
            <Tabs.Tab value="inflight">Logins in flight</Tabs.Tab>
            <Tabs.Tab value="lockouts">Lockouts</Tabs.Tab>
            <Tabs.Tab value="activity">Activity</Tabs.Tab>
            <Tabs.Tab value="trusted">Trusted devices</Tabs.Tab>
            <Tabs.Tab value="overrides">2FA overrides</Tabs.Tab>
          </Tabs.List>
          <div className="flex items-center gap-3">
            {/* Fixed-width countdown to the left so the checkbox never shifts. */}
            <span className="w-7 text-right text-xs text-gray-500">
              {autoRefresh ? `${countdown}s` : ''}
            </span>
            <Checkbox
              size="xs"
              label="Auto refresh"
              classNames={{ label: '!pl-2' }}
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.currentTarget.checked)}
            />
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => {
                refreshAll()
                setCountdown(AUTO_REFRESH_SECONDS)
              }}
            >
              Refresh
            </Button>
          </div>
        </div>

        <Tabs.Panel value="overview" pt="md">
          {previews.map((p) => (
            <OverviewSection
              key={p.tab}
              title={p.title}
              shown={p.shown}
              total={p.total}
              onSeeAll={() => goTo(p.tab)}
            >
              {p.table}
            </OverviewSection>
          ))}
        </Tabs.Panel>

        <Tabs.Panel value="sessions" pt="md">
          <Paper withBorder>
            <SessionsTable
              items={sessions.items}
              onForceLogout={confirmForceLogout}
              onForceLogoutAll={confirmForceLogoutAll}
            />
            <ScrollSentinel enabled={sessions.hasMore} onVisible={sessions.loadMore} />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="lockouts" pt="md">
          <Paper withBorder>
            <LockoutsTable items={lockouts.items} onClear={clearLockout} />
            <ScrollSentinel enabled={lockouts.hasMore} onVisible={lockouts.loadMore} />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="trusted" pt="md">
          <Paper withBorder>
            <TrustedDevicesTable items={devices.items} onRevoke={revokeTrustedDevice} />
            <ScrollSentinel enabled={devices.hasMore} onVisible={devices.loadMore} />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="inflight" pt="md">
          <Paper withBorder>
            <ChallengesTable items={challenges.items} onVoid={voidChallenge} />
            <ScrollSentinel enabled={challenges.hasMore} onVisible={challenges.loadMore} />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="activity" pt="md">
          <div className="mb-2 flex justify-end">
            <Select
              size="xs"
              clearable
              placeholder="All events"
              data={[...AUTH_AUDIT_EVENT_TYPE_VALUES]}
              value={eventType}
              onChange={setEventType}
            />
          </div>
          <Paper withBorder>
            <ActivityTable items={events.items} />
            <ScrollSentinel enabled={events.hasMore} onVisible={events.loadMore} />
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="overrides" pt="md">
          <p className="mb-3 text-xs text-gray-500">
            Accounts on this list still receive the real code by SMS/email, but only the fixed code
            below verifies. Every override expires — 1 to 365 days.
          </p>
          <AddOtpOverrideForm onSubmit={setOtpOverride} />
          <Paper withBorder>
            <OtpOverridesTable items={overrides.items} onRemove={confirmRemoveOtpOverride} />
            <ScrollSentinel enabled={overrides.hasMore} onVisible={overrides.loadMore} />
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}
